import { useMemo } from 'react';

import type {
  OpportunityRelatedDeal,
  OpportunityRelatedExtraGuestSurcharge,
  OpportunityRelatedInclusion,
} from '~/hooks/apis/offers/useGetOpportunityRelated';

import type { SalesforceSurchargePeriod } from '~/types/surcharges';

import { withChecklistItemMetadata } from './checklistItemMetadata';
import { buildBlackoutRows, calculateBlackoutCoverage } from './tabs/blackoutDates/blackoutRules';
import { buildBundledExperienceRows } from './tabs/bundledExperiences/bundledExperiencesRules';
import { buildCampaignRows } from './tabs/campaign/campaignRules';
import { buildDatesRestrictionsRows } from './tabs/datesRestrictions/datesRestrictionsRules';
import {
  buildDealOptionsInclusionsDealRows,
  buildDealOptionsInclusionsRows,
} from './tabs/dealOptionsInclusions/dealOptionsInclusionsRules';
import { buildEgsCapacityRows, buildEgsMarginRows } from './tabs/egs/egsRules';
import { buildSetupChecklistRules } from './tabs/setup/setupRules';
import type { SetupRuleInput } from './tabs/setup/types';
import { buildSurchargeTableRows, buildSurchargesRows } from './tabs/surcharges/surchargesRules';
import type { BlackoutDateRange } from './tabs/surcharges/types';
import { buildTaxesPaymentsRows } from './tabs/taxesPayments/taxesPaymentsRules';

interface Input {
  blackoutDates?: Array<BlackoutDateRange>;
  campaignType?: string;
  offer?: App.Offer;
  opportunity?: App.OpportunityPage;
  opportunityContract?: App.Contract;
  opportunityError?: unknown;
  packages?: Array<App.Package>;
  propertyError?: unknown;
  propertyHasTaxesPayableAtProperty: boolean;
  propertyId?: string;
  propertyRoomTypes?: Array<App.RoomType>;
  relatedDeals?: Array<OpportunityRelatedDeal>;
  relatedExtraGuestSurcharges?: Array<OpportunityRelatedExtraGuestSurcharge>;
  relatedInclusions?: Array<OpportunityRelatedInclusion>;
  setupInput: SetupRuleInput;
  surcharges: Array<SalesforceSurchargePeriod>;
  travelFromDate?: string;
  travelToDate?: string;
}

export function useCMChecklistRows({
  blackoutDates,
  campaignType,
  offer,
  opportunity,
  opportunityContract,
  opportunityError,
  packages,
  propertyError,
  propertyHasTaxesPayableAtProperty,
  propertyId,
  propertyRoomTypes,
  relatedDeals,
  relatedExtraGuestSurcharges,
  relatedInclusions,
  setupInput,
  surcharges,
  travelFromDate,
  travelToDate,
}: Input) {
  const opportunityTravelFrom = opportunity?.key_dates?.expiry_date_travel_from__c ?? travelFromDate;
  const opportunityTravelTo = opportunity?.key_dates?.travel_to__c ?? travelToDate;
  const opportunityBlackoutDates = opportunity?.black_out_dates;
  const dealCurrency = getOfferCurrency(packages);

  const blackoutRows = useMemo(
    () =>
      withChecklistItemMetadata(
        'blackoutDates',
        buildBlackoutRows({
          blackOutDates: opportunity?.black_out_dates,
          hasSourceError: !!opportunityError,
          travelFrom: opportunityTravelFrom,
          travelTo: opportunityTravelTo,
        }),
      ),
    [opportunity?.black_out_dates, opportunityError, opportunityTravelFrom, opportunityTravelTo],
  );

  const setupRows = useMemo(
    () => withChecklistItemMetadata('setup', buildSetupChecklistRules(setupInput)),
    [setupInput],
  );
  const marginRows = useMemo(
    () => withChecklistItemMetadata('egs', buildEgsMarginRows(relatedExtraGuestSurcharges)),
    [relatedExtraGuestSurcharges],
  );
  const capacityRows = useMemo(
    () =>
      withChecklistItemMetadata(
        'egs',
        buildEgsCapacityRows({
          hasEgsMarginRows: marginRows.length > 0,
          packages,
          propertyRoomTypes,
          relatedExtraGuestSurcharges,
          surcharges,
        }),
      ),
    [marginRows.length, packages, propertyRoomTypes, relatedExtraGuestSurcharges, surcharges],
  );
  const baseSurchargesRows = useMemo(
    () => withChecklistItemMetadata('surcharges', buildSurchargesRows({ surcharges, travelFromDate, travelToDate })),
    [surcharges, travelFromDate, travelToDate],
  );
  const surchargeTableRows = useMemo(
    () =>
      withChecklistItemMetadata(
        'surcharges',
        buildSurchargeTableRows({ blackoutDates, packages, surcharges, travelFromDate, travelToDate }),
      ),
    [blackoutDates, packages, surcharges, travelFromDate, travelToDate],
  );
  const propertyDataAvailable = propertyId !== undefined && !propertyError;
  const taxesPaymentsRows = useMemo(
    () =>
      withChecklistItemMetadata(
        'taxesPayments',
        buildTaxesPaymentsRows({
          cityOtherTax: opportunityContract?.city_other_tax__c,
          cityTaxComments: opportunityContract?.city_tax_comments__c,
          hasPropertyTaxesPayableAtProperty: propertyHasTaxesPayableAtProperty,
          opportunityDataAvailable: !!opportunityContract,
          paymentMethod: opportunityContract?.payment_method__c,
          paymentMilestone: opportunityContract?.payment_milestone__c,
          prizeOrderId: opportunityContract?.prize_order_id__c,
          propertyDataAvailable,
        }),
      ),
    [opportunityContract, propertyHasTaxesPayableAtProperty, propertyDataAvailable],
  );
  const campaignRows = useMemo(
    () =>
      withChecklistItemMetadata(
        'campaign',
        buildCampaignRows({ offer, opportunity, opportunityDeals: relatedDeals, setupInput }),
      ),
    [offer, opportunity, relatedDeals, setupInput],
  );
  const datesRestrictionsRows = useMemo(
    () =>
      withChecklistItemMetadata(
        'datesRestrictions',
        buildDatesRestrictionsRows({ campaignType, opportunityDeals: relatedDeals, packages }),
      ),
    [campaignType, relatedDeals, packages],
  );
  const bundledExperienceRows = useMemo(
    () =>
      withChecklistItemMetadata(
        'bundledExperiences',
        buildBundledExperienceRows({
          dealCurrency,
          hasOpportunitySourceError: !!opportunityError,
          hotelBlackoutDates: opportunityBlackoutDates,
          hotelTravelFrom: opportunityTravelFrom,
          hotelTravelTo: opportunityTravelTo,
          relatedDeals,
        }),
      ),
    [
      dealCurrency,
      opportunityBlackoutDates,
      opportunityError,
      opportunityTravelFrom,
      opportunityTravelTo,
      relatedDeals,
    ],
  );

  const dealOptionsInclusionsRows = useMemo(
    () =>
      withChecklistItemMetadata(
        'dealOptionsInclusions',
        buildDealOptionsInclusionsRows({
          dealDataAvailable: relatedDeals !== undefined,
          relatedDeals: relatedDeals ?? [],
          relatedInclusions: relatedInclusions ?? [],
          relatedInclusionsAvailable: relatedInclusions !== undefined,
        }),
      ),
    [relatedDeals, relatedInclusions],
  );
  const dealOptionsInclusionsDealRows = useMemo(
    () =>
      buildDealOptionsInclusionsDealRows({
        flatRows: dealOptionsInclusionsRows,
        relatedDeals: relatedDeals ?? [],
      }),
    [dealOptionsInclusionsRows, relatedDeals],
  );
  const blackoutCoverage = useMemo(
    () =>
      calculateBlackoutCoverage({
        blackOutDates: blackoutRows,
        travelFrom: opportunityTravelFrom,
        travelTo: opportunityTravelTo,
      }),
    [blackoutRows, opportunityTravelFrom, opportunityTravelTo],
  );

  return {
    baseSurchargesRows,
    blackoutCoverage,
    blackoutRows,
    bundledExperienceRows,
    campaignRows,
    capacityRows,
    datesRestrictionsRows,
    dealOptionsInclusionsDealRows,
    dealOptionsInclusionsRows,
    marginRows,
    opportunityTravelFrom,
    opportunityTravelTo,
    setupRows,
    surchargeTableRows,
    taxesPaymentsRows,
  };
}

function getOfferCurrency(packages?: Array<App.Package>) {
  return packages?.find((offerPackage) => offerPackage.cost_currency)?.cost_currency;
}
